import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Mail, Paperclip, X, Send, ExternalLink, Loader2, Check, AlertTriangle } from 'lucide-react'
import { labelCls, inputCls } from '../styles'
import { vendorRegistrationApi } from '@api/client'
import { VENDOR_REGISTRATIONS, fillTemplate } from '../vendorRegistrationData'
import type { VendorRegistration } from '../vendorRegistrationData'

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

interface VendorCardProps {
  vendor: VendorRegistration
  companyName: string
}

function VendorCard({ vendor, companyName }: VendorCardProps) {
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)
  const [extraValues, setExtraValues] = useState<Record<string, string>>({})
  const [subject, setSubject] = useState(() => fillTemplate(vendor.subjectTemplate, { companyName }))
  const [body, setBody] = useState(() => fillTemplate(vendor.bodyTemplate, { companyName }))
  const [dirty, setDirty] = useState(false)
  const [attachments, setAttachments] = useState<{ filename: string; content: string }[]>([])
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<'ok' | 'error' | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)

  function refillTemplates(nextExtra: Record<string, string>) {
    const values = { companyName, ...nextExtra }
    if (!dirty) {
      setSubject(fillTemplate(vendor.subjectTemplate, values))
      setBody(fillTemplate(vendor.bodyTemplate, values))
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refillTemplates(extraValues) }, [companyName])

  function updateExtra(key: string, value: string) {
    const next = { ...extraValues, [key]: value }
    setExtraValues(next)
    refillTemplates(next)
  }

  async function handleFiles(files: FileList | null) {
    if (!files) return
    const encoded = await Promise.all(Array.from(files).map(async (f) => ({ filename: f.name, content: await fileToBase64(f) })))
    setAttachments((prev) => [...prev, ...encoded])
  }

  function removeAttachment(filename: string) {
    setAttachments((prev) => prev.filter((a) => a.filename !== filename))
  }

  const mailtoHref = `mailto:${vendor.to.join(',')}${vendor.cc ? `?cc=${vendor.cc.join(',')}&` : '?'}subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

  async function handleSend() {
    setSending(true)
    setSendResult(null)
    setSendError(null)
    try {
      const html = body.split('\n').map((line) => `<p style="margin:0 0 8px">${line || '&nbsp;'}</p>`).join('')
      const res = await vendorRegistrationApi.sendEmail({ to: vendor.to, cc: vendor.cc, subject, html, attachments })
      if (res.success) {
        setSendResult('ok')
      } else {
        setSendResult('error')
        setSendError(res.error || 'Failed to send')
      }
    } catch {
      setSendResult('error')
      setSendError('Network error')
    } finally {
      setSending(false)
      setConfirming(false)
    }
  }

  return (
    <div className="rounded-[12px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#f8fafc' }}>
      <div className="w-full flex items-center gap-2 px-3.5 py-2.5 transition-colors hover:bg-[#ffffff]">
        <input
          type="checkbox"
          checked={done}
          onChange={(e) => setDone(e.target.checked)}
          style={{ accentColor: '#6366f1' }}
          aria-label={`Mark ${vendor.name} as done`}
        />
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex-1 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Mail size={12} className="text-[#4f46e5]" />
            <span className={`text-[11px] font-semibold ${done ? 'text-[#64748b] line-through' : 'text-[#1e293b]'}`}>{vendor.name}</span>
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(96,165,250,.12)', color: '#2563eb' }}>{vendor.country}</span>
            <span className="text-[9px] text-[#475569]">SLA: {vendor.sla}</span>
          </div>
          {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
        </button>
      </div>

      {open && (
        <div className="p-3.5 space-y-3" style={{ borderTop: '1px solid #e5e7eb' }}>
          {vendor.note && <p className="text-[10px] text-[#64748b] italic">{vendor.note}</p>}

          <div className="text-[10px] text-[#475569] space-y-0.5">
            <p><span className="text-[#64748b]">To:</span> {vendor.to.join(', ')}</p>
            {vendor.cc && <p><span className="text-[#64748b]">Cc:</span> {vendor.cc.join(', ')}</p>}
          </div>

          {vendor.extraFields.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {vendor.extraFields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  <input className={inputCls} placeholder={f.placeholder} value={extraValues[f.key] ?? ''} onChange={(e) => updateExtra(f.key, e.target.value)} />
                </div>
              ))}
            </div>
          )}

          <div>
            <label className={labelCls}>Subject</label>
            <input className={inputCls} value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true) }} />
          </div>
          <div>
            <label className={labelCls}>Body</label>
            <textarea className={inputCls} rows={8} value={body} onChange={(e) => { setBody(e.target.value); setDirty(true) }} />
          </div>

          <div>
            <label className={labelCls}>Attachments needed (prepare these separately — can&apos;t be generated here)</label>
            <ul className="space-y-0.5">
              {vendor.attachmentsNeeded.map((a) => (
                <li key={a} className="text-[10px] text-[#475569] flex items-start gap-1.5">
                  <Paperclip size={9} className="mt-0.5 shrink-0 text-[#475569]" />
                  {a}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <label className={labelCls}>Attach files</label>
            <input type="file" multiple onChange={(e) => void handleFiles(e.target.files)} className="text-[10px] text-[#64748b]" />
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {attachments.map((a) => (
                  <span key={a.filename} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                    {a.filename}
                    <button onClick={() => removeAttachment(a.filename)} className="hover:text-red-700"><X size={9} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <a
              href={mailtoHref}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[8px] text-[11px] font-semibold transition-colors"
              style={{ background: 'rgba(99,102,241,.14)', border: '1px solid rgba(99,102,241,.4)', color: '#4f46e5' }}
            >
              <ExternalLink size={12} /> Open in Email Client
            </a>

            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[8px] text-[11px] font-semibold transition-colors"
                style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#64748b' }}
              >
                <Send size={12} /> Send via DealHub
              </button>
            ) : (
              <div className="flex-1 flex items-center gap-1.5">
                <button
                  onClick={() => void handleSend()}
                  disabled={sending}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[8px] text-[11px] font-semibold transition-colors disabled:opacity-50"
                  style={{ background: 'rgba(52,211,153,.15)', border: '1px solid rgba(52,211,153,.4)', color: '#15803d' }}
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Confirm send to {vendor.to[0]}{vendor.to.length > 1 ? ` +${vendor.to.length - 1}` : ''}
                </button>
                <button onClick={() => setConfirming(false)} disabled={sending} className="px-2 py-2 text-[#64748b] hover:text-red-700"><X size={12} /></button>
              </div>
            )}
          </div>

          {sendResult === 'ok' && (
            <p className="text-[10px] text-emerald-700 flex items-center gap-1"><Check size={10} /> Sent — Resend uses a shared sandbox domain, so delivery to external addresses isn&apos;t guaranteed unless a verified domain is configured.</p>
          )}
          {sendResult === 'error' && (
            <p className="text-[10px] text-red-700 flex items-center gap-1"><AlertTriangle size={10} /> {sendError}</p>
          )}
        </div>
      )}
    </div>
  )
}

interface VendorRegistrationPanelProps {
  companyName: string
}

export function VendorRegistrationPanel({ companyName }: VendorRegistrationPanelProps) {
  const [open, setOpen] = useState(true)

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <Mail size={12} className="text-[#4f46e5]" />
          Vendor Registration — Due Diligence Emails
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569]">
            Real emails to real external partners — &quot;Open in Email Client&quot; is the safe default (uses your own verified mailbox and lets you attach the signed forms). &quot;Send via DealHub&quot; sends through this app&apos;s shared Resend sandbox address, which may not deliver to external inboxes unless a custom domain is verified.
            {!companyName && ' Enter a customer account name above to prefill these templates.'}
          </p>
          <div className="space-y-2">
            {VENDOR_REGISTRATIONS.map((v) => (
              <VendorCard key={v.id} vendor={v} companyName={companyName || '[Company Name]'} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
